// Pure geometry: mapping a pointer position over a chart's plot area to a
// bucket index, and placing a floating tooltip near that pointer without
// covering the mark or getting clipped by an ancestor's overflow. Built ONCE
// here because two different features need the exact same first step
// (pointer -> bucket index): the floating hover tooltip on every chart
// primitive (chart-bar.tsx/chart-line-area.tsx) and the BI app's
// drag-to-zoom time brush (frontend/bi/src/lib/time-brush.ts, which layers
// "bucket range -> date bounds" on top of the index math here).
//
// Every chart primitive in this file family lays buckets out on a FIXED,
// viewBox-relative axis with no left/right padding (`W = 480`, see
// chart-bar.tsx/chart-line-area.tsx) — so a pointer's FRACTION across the
// rendered element's width maps linearly to a bucket index regardless of how
// the SVG is scaled on screen (CSS `width: 100%`); nothing here needs to know
// the actual viewBox units.

/** A `DOMRect`-shaped box — deliberately narrower than the real `DOMRect`
 *  interface so a caller (or a test) can pass a plain object without
 *  constructing one. */
export interface HoverBox {
  left: number;
  width: number;
}

/** clientX relative to `box` -> a 0..1 fraction across its width. Clamped, so
 *  a pointer that has strayed slightly outside the box (a fast drag past the
 *  edge, a sub-pixel rounding gap) still resolves to an in-range fraction
 *  rather than a value every caller would otherwise have to re-clamp itself.
 *  A zero-or-negative-width box (an unmeasured/unmounted element — jsdom's
 *  default, notably) resolves to 0 rather than `NaN`/`Infinity`. */
export function fractionAcross(clientX: number, box: HoverBox): number {
  if (box.width <= 0) return 0;
  return clamp01((clientX - box.left) / box.width);
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(n, 1));
}

/** "step": `bucketCount` equal-width steps, no padding (`BarChart`'s own
 *  layout — `step = W / n`) — the fraction falls INTO exactly one step, the
 *  same shape as the bar it's hovering.
 *  "nearest": `bucketCount` points evenly spaced edge-to-edge
 *  (`LineAreaChart`'s `x(i) = (i / (bucketCount - 1)) * W`) — the fraction
 *  snaps to the CLOSEST point, matching how a line chart reads (the mark IS
 *  the data point, not a region of the axis). */
export type BucketIndexMode = "step" | "nearest";

/** Maps a 0..1 fraction across the plot width to a bucket index, or `null`
 *  for a chart with no buckets at all (nothing to hover/brush). Fractions
 *  outside `[0, 1]` clamp to the nearest end bucket rather than returning
 *  `null` — a drag that overshoots the chart edge still resolves to a real
 *  bucket (the design doc's "handle pointer-up outside the chart" rule,
 *  shared by the hover tooltip and the time brush alike). */
export function bucketIndexFromFraction(
  fraction: number,
  bucketCount: number,
  mode: BucketIndexMode,
): number | null {
  if (bucketCount <= 0) return null;
  const f = clamp01(fraction);
  if (mode === "step") {
    return Math.min(Math.floor(f * bucketCount), bucketCount - 1);
  }
  const denom = Math.max(bucketCount - 1, 1);
  return Math.min(Math.max(Math.round(f * denom), 0), bucketCount - 1);
}

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Positions a floating tooltip near `anchor` (the pointer's viewport
 * position) inside a `viewport` of size `viewport`, sized `tooltip`. Offsets
 * so the tooltip never sits ON TOP of the point it describes (default:
 * above-and-right of the anchor) and never runs past the viewport edge —
 * this returns VIEWPORT-relative coordinates specifically so a
 * `position: fixed` portal (never `absolute` inside the card) is what
 * consumes them, which is what keeps the tooltip from ever being clipped by
 * a card's `overflow`.
 *
 * Flips left when there's no room to the right, flips below when there's no
 * room above; when even the flipped placement would still overflow (a tiny
 * viewport, a huge tooltip), clamps fully on-screen as a last resort rather
 * than letting either edge run off. `gap` is the clearance kept between the
 * anchor point and the tooltip's nearest edge.
 */
export function placeTooltip(anchor: Point, tooltip: Size, viewport: Size, gap = 12): Point {
  let x = anchor.x + gap;
  if (x + tooltip.width > viewport.width) {
    x = anchor.x - tooltip.width - gap;
  }
  x = Math.max(0, Math.min(x, Math.max(viewport.width - tooltip.width, 0)));

  let y = anchor.y - tooltip.height - gap;
  if (y < 0) {
    y = anchor.y + gap;
  }
  y = Math.max(0, Math.min(y, Math.max(viewport.height - tooltip.height, 0)));

  return { x, y };
}
